import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaChevronLeft, FaPlus } from 'react-icons/fa'
import {
  createEmailTemplate,
  deleteEmailTemplate,
  EmailTemplate,
  EmailTemplateForm,
  EmailTemplateKind,
  getEmailTemplates,
  updateEmailTemplate,
} from '../../data/emailTemplateData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import './InvoiceEmailSettings.css'

const blankForm: EmailTemplateForm = { name: '', subject: '', body: '', isDefault: false }

const formOf = (t: EmailTemplate): EmailTemplateForm => ({
  name: t.name,
  subject: t.subject,
  body: t.body,
  isDefault: t.isDefault,
})

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

// The wording that differs between the kinds of email.
const TEXT: Record<EmailTemplateKind, { title: string; lead: string; noun: string; defaultLabel: string; fillHint: string }> = {
  Invoice: {
    title: 'Invoice Email Templates',
    lead:
      'Each invoice has one of these chosen on it, and is emailed with its subject and message - the invoice PDF is attached automatically. New invoices start on the default. They are shared by everybody.',
    noun: 'invoices',
    defaultLabel: 'Default for new invoices',
    fillHint: 'Filled in for each invoice. Click one to insert it.',
  },
  Quote: {
    title: 'Quote Email Templates',
    lead:
      'The wording quotes are emailed with, from hello@. When someone emails a quote they start from the default, can switch to another, and can change the wording before it goes. They are shared by everybody.',
    noun: 'quote emails',
    defaultLabel: 'Default for quote emails',
    fillHint: 'Filled in for each quote. Click one to insert it.',
  },
}

/**
 * The email templates for one kind of email. Invoices each have one chosen on
 * them and start on the default; quote emails start on the default and can be
 * switched and reworded when sent. Placeholders such as {InvoiceNumber} are
 * filled in per invoice or quote.
 */
const InvoiceEmailSettings = ({ kind = 'Invoice' }: { kind?: EmailTemplateKind }) => {
  const text = TEXT[kind]
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [templates, setTemplates] = useState<EmailTemplate[] | null>(null)
  const [placeholders, setPlaceholders] = useState<Record<string, string>>({})
  const [loadError, setLoadError] = useState<string | null>(null)

  // 'new' while adding one; otherwise the id being edited.
  const [selected, setSelected] = useState<number | 'new' | null>(null)
  const [form, setForm] = useState<EmailTemplateForm>(blankForm)
  const [busy, setBusy] = useState(false)

  // Placeholders insert at the cursor of whichever field was last focused.
  const lastField = useRef<'subject' | 'body'>('body')
  const subjectRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const current = typeof selected === 'number' ? templates?.find((t) => t.emailTemplateId === selected) ?? null : null
  const saved = selected === 'new' ? blankForm : current ? formOf(current) : blankForm
  const dirty = JSON.stringify(form) !== JSON.stringify(saved)
  const valid = !!form.name.trim() && !!form.subject.trim() && !!form.body.trim()

  const load = useCallback(async (select?: number) => {
    try {
      const result = await getEmailTemplates(kind)
      setTemplates(result.templates)
      setPlaceholders(result.placeholders)
      const next = result.templates.find((t) => t.emailTemplateId === select) ?? result.templates[0]
      setSelected(next ? next.emailTemplateId : 'new')
      setForm(next ? formOf(next) : { ...blankForm, isDefault: true })
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "The email templates couldn't be loaded.").message)
    }
  }, [kind])

  useEffect(() => {
    load()
  }, [load])

  const confirmLeave = () => !dirty || window.confirm('You have unsaved changes to this template. Discard them?')

  const select = (t: EmailTemplate) => {
    if (t.emailTemplateId === selected || !confirmLeave()) return
    setSelected(t.emailTemplateId)
    setForm(formOf(t))
  }

  const startNew = () => {
    if (!confirmLeave()) return
    setSelected('new')
    setForm({ ...blankForm, isDefault: (templates?.length ?? 0) === 0 })
  }

  const insertPlaceholder = (placeholder: string) => {
    const key = lastField.current
    const field = key === 'subject' ? subjectRef.current : bodyRef.current
    const value = form[key]
    const start = field?.selectionStart ?? value.length
    const end = field?.selectionEnd ?? value.length
    setForm({ ...form, [key]: value.slice(0, start) + placeholder + value.slice(end) })

    // Put the cursor back after what was inserted.
    requestAnimationFrame(() => {
      field?.focus()
      field?.setSelectionRange(start + placeholder.length, start + placeholder.length)
    })
  }

  const handleSave = async () => {
    setBusy(true)
    try {
      const result =
        selected === 'new'
          ? await createEmailTemplate(kind, form)
          : await updateEmailTemplate(kind, selected as number, form)
      showToast(selected === 'new' ? 'Template added' : 'Template saved', 'success')
      await load(result.emailTemplateId)
    } catch (error) {
      showToast(getApiErrorDetails(error, "The template couldn't be saved.").message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const handleDelete = async () => {
    if (!current) return
    const inUse =
      current.invoiceCount > 0
        ? ` ${current.invoiceCount} invoice${current.invoiceCount === 1 ? ' is' : 's are'} set to use it and will keep it.`
        : ''
    if (!window.confirm(`Remove the "${current.name}" template? It won't be offered for ${text.noun} any more.${inUse}`)) return
    setBusy(true)
    try {
      await deleteEmailTemplate(kind, current.emailTemplateId)
      showToast('Template removed', 'success')
      await load()
    } catch (error) {
      showToast(getApiErrorDetails(error, "The template couldn't be removed.").message, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className='ies-page'>
      <div className='ies-inner'>
        <button type='button' className='ies-crumb' onClick={() => confirmLeave() && navigate('/management')}>
          <FaChevronLeft /> Management
        </button>
        <h1 className='ies-title'>{text.title}</h1>
        <p className='ies-lead'>{text.lead}</p>

        {loadError ? (
          <div className='ies-error'>{loadError}</div>
        ) : !templates ? (
          <p className='ies-muted'>Loading…</p>
        ) : (
          <div className='ies-layout'>
            <nav className='ies-card ies-list' aria-label='Templates'>
              {templates.map((t) => (
                <button
                  key={t.emailTemplateId}
                  type='button'
                  className={`ies-list__item${selected === t.emailTemplateId ? ' ies-list__item--active' : ''}`}
                  onClick={() => select(t)}
                >
                  <span className='ies-list__name'>
                    {t.name}
                    {t.isDefault && <span className='ies-badge'>Default</span>}
                  </span>
                  {kind === 'Invoice' && (
                    <span className='ies-list__meta'>
                      {t.invoiceCount} invoice{t.invoiceCount === 1 ? '' : 's'}
                    </span>
                  )}
                </button>
              ))}
              {selected === 'new' && (
                <div className='ies-list__item ies-list__item--active'>
                  <span className='ies-list__name'>{form.name.trim() || 'New template'}</span>
                </div>
              )}
              <button type='button' className='ies-btn ies-btn--secondary ies-list__add' onClick={startNew} disabled={busy}>
                <FaPlus /> New template
              </button>
            </nav>

            <section className='ies-card'>
              <label className='ies-label' htmlFor='ies-name'>Name</label>
              <input
                id='ies-name'
                className='ies-input'
                value={form.name}
                maxLength={100}
                placeholder='e.g. Standard, Final account, Reminder'
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <label className='ies-label' htmlFor='ies-subject'>Subject</label>
              <input
                id='ies-subject'
                ref={subjectRef}
                className='ies-input'
                value={form.subject}
                maxLength={500}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                onFocus={() => (lastField.current = 'subject')}
              />

              <label className='ies-label' htmlFor='ies-body'>Message</label>
              <textarea
                id='ies-body'
                ref={bodyRef}
                className='ies-input ies-textarea'
                value={form.body}
                rows={12}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                onFocus={() => (lastField.current = 'body')}
              />
              <p className='ies-hint'>Leave a blank line between paragraphs.</p>

              <label className='ies-check'>
                <input
                  type='checkbox'
                  checked={form.isDefault}
                  // The default can't be unticked - making another the default replaces it.
                  disabled={!!current?.isDefault}
                  onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                />
                {text.defaultLabel}
              </label>

              <div className='ies-footer'>
                <span className='ies-muted'>
                  {current?.updatedDate &&
                    `Last changed ${formatDateTime(current.updatedDate)}${current.updatedBy ? ` by ${current.updatedBy}` : ''}`}
                </span>
                <div className='ies-actions'>
                  {current && !current.isDefault && (
                    <button type='button' className='ies-btn ies-btn--danger' onClick={handleDelete} disabled={busy}>
                      Remove
                    </button>
                  )}
                  <button
                    type='button'
                    className='ies-btn ies-btn--secondary'
                    onClick={() => setForm(saved)}
                    disabled={!dirty || busy}
                  >
                    Discard changes
                  </button>
                  <button
                    type='button'
                    className='ies-btn ies-btn--primary'
                    onClick={handleSave}
                    disabled={!dirty || !valid || busy}
                  >
                    {busy ? 'Saving…' : selected === 'new' ? 'Add template' : 'Save'}
                  </button>
                </div>
              </div>
            </section>

            <aside className='ies-card ies-placeholders'>
              <h2 className='ies-subtitle'>Placeholders</h2>
              <p className='ies-hint'>{text.fillHint}</p>
              <ul>
                {Object.entries(placeholders).map(([placeholder, meaning]) => (
                  <li key={placeholder}>
                    <button type='button' className='ies-chip' onClick={() => insertPlaceholder(placeholder)}>
                      {placeholder}
                    </button>
                    <span>{meaning}</span>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        )}
      </div>
    </div>
  )
}

export default InvoiceEmailSettings
